import {
  AppFrame,
  Button,
  hueOf,
  Icon,
  type IconName,
  initialsOf,
  MEDIA_QUERIES,
  Menu,
  OrgMark,
  PlaceRail,
  Tooltip,
  useMediaQuery,
} from '@wtfalch/design';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import { type Agora, chatEnabled, tasksEnabled } from '../agoras.ts';
import { CallDock } from '../calls/CallDock.tsx';
import { APP_ICONS, applyAppIcon, savedAppIcon } from '../design/appIcon.ts';
import { isDesktop } from '../desktop-auth.ts';
import {
  type ReminderPermission,
  reminderPermission,
  requestReminderPermission,
} from '../reminders.ts';
import {
  isPlainClick,
  pathForAdd,
  pathForSection,
  pathForSettings,
  type Section,
} from '../routes.ts';
import { SearchField, type SearchFieldClients } from '../search/SearchField.tsx';
import { ShortcutsDialog, useGlobalShortcuts } from '../shortcuts.tsx';
import { stickerOf } from '../ui/Avatar.tsx';

/**
 * The window, as the design canvas draws it: a place rail on the paper, and
 * the work as one white card floating on it, with its own header.
 *
 *     [place rail] [ card: header -- company / section title ... account ]
 *                  [       body                                           ]
 *
 * Six places, always the same six, always in the same spot: Today, Mail,
 * Chat, Calendar, Files, Tasks. Each is a tile -- an icon over its name --
 * and the one you are in is lifted onto the card's white with a dot under it.
 *
 * **A place stays listed rather than disappearing.** Files ships this way --
 * dimmed to `.45`, unclickable, the reason on hover -- until whatever backs
 * it says yes (`offReason`, below). Chat and Tasks are the other kind: a
 * company's config either turns them on or they are not in the rail at all
 * (`chatEnabled`, `tasksEnabled`).
 *
 * **No header band across the window, and no list-style sidebar.** Those are
 * Boule's shape: Boule is a directory of many pages, Agora is six places.
 *
 * **No company rail on the web.** The desktop and phone apps hold several
 * companies and put a rail of their marks in front of this one. A browser tab
 * is one company; its mark and name open the card's header, and a person with
 * more than one switches there.
 *
 * On a phone the rail becomes a tab bar along the bottom, the card loses its
 * margin, and the header keeps the company and the section.
 */

const PLACES: readonly { section: Section; label: string; icon: IconName }[] = [
  { section: 'today', label: 'Today', icon: 'home' },
  { section: 'mail', label: 'Mail', icon: 'mail' },
  { section: 'chat', label: 'Chat', icon: 'chat' },
  { section: 'calendar', label: 'Calendar', icon: 'calendar' },
  { section: 'files', label: 'Files', icon: 'folder' },
  { section: 'tasks', label: 'Tasks', icon: 'check' },
];

const TITLES: Record<Section, string> = {
  today: 'Today',
  mail: 'Mail',
  chat: 'Chat',
  calendar: 'Calendar',
  files: 'Files',
  tasks: 'Tasks',
};

/** Why a place is drawn in the rail but cannot be opened -- Chat's own
 *  reason (agora#280) generalised: a section stays listed rather than
 *  disappearing, with a tile that says why, until whatever backs it says
 *  yes. */
function offReason(
  section: Section,
  chatAvailable: boolean | null,
  filesAvailable: boolean,
): string | null {
  if (section === 'chat' && chatAvailable === false) return 'Chat is not available yet';
  if (section === 'files' && !filesAvailable) return 'Files is not turned on yet';
  return null;
}

export function AgoraShell({
  agoras,
  current,
  section,
  title,
  chatAvailable,
  filesAvailable,
  mailUnread,
  chatUnread,
  calendarUnread,
  account,
  onSignOut,
  actions,
  searchClients,
  go,
  children,
}: {
  agoras: Agora[];
  /** The open company, or null on the screens before one is open. */
  current: Agora | null;
  section: Section | null;
  /** Overrides the section's own fixed title -- Mail's header reading
   *  "Write" while its composer is open, the way the design canvas's own
   *  `Compose.dc.html` titles it, rather than staying "Mail". Falls back to
   *  `TITLES[section]` when omitted, which is every other screen. */
  title?: string;
  /** Whether this company's token carries the messaging audience. Chat is
   *  left off the rail otherwise, rather than drawn and refused. `null`
   *  while the chat client is still asking: the tile is drawn as usual then,
   *  not greyed for the second or two it takes to answer. */
  chatAvailable: boolean | null;
  /** Whether `App` has a `FilesSource` to back this section -- there is no
   *  real one yet (`files/source.ts`'s own doc), so this is false outside
   *  the fake (agora#280's same "stay in the rail, disabled" treatment). */
  filesAvailable: boolean;
  /** The inbox's own unread-thread count, for the Mail tile's rust badge --
   *  `PlaceRail.Place`'s own `badge` (agora#219). */
  mailUnread?: number | null;
  /** Every unmuted room's unread count, summed (`counts.ts`'s
   *  `chatUnreadTotal`), for the Chat tile's badge. */
  chatUnread?: number | null;
  /** Upcoming invitations not yet answered (`counts.ts`'s
   *  `calendarNeedsActionCount`), for the Calendar tile's badge. */
  calendarUnread?: number | null;
  /** Who is signed in, once somebody is. */
  account: { name: string | null; address: string } | null;
  onSignOut: (() => void) | null;
  /** The section's own controls, at the right of the header. */
  actions?: ReactNode;
  /** The combined search's own clients (agora#216) -- reported up by
   *  `App.tsx`'s `AgoraView` the same way `onAccount`/`onMailUnread` already
   *  are, since the search field lives in this shell rather than inside any
   *  one section. `null` before a client exists (no session yet, or a section
   *  that has not created one), which just means fewer groups can run. */
  searchClients?: SearchFieldClients;
  go: (path: string) => void;
  children: ReactNode;
}) {
  const [appIcon, setAppIcon] = useState(savedAppIcon);
  // A phone's header has no room for the field, so it is an icon there that
  // opens the field over the row (`app.css`'s `data-open`); on a wide screen
  // the field is always in the header and this state does nothing.
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (searchOpen) searchRef.current?.querySelector('input')?.focus();
  }, [searchOpen]);
  // Meeting reminders (T6, D7) and chat notifications (T4, MSG-022) share
  // one browser permission -- `Notification.permission` is per origin, not
  // per feature -- so this one menu item's click is what asks for both.
  // Never on load, only from this click, a user gesture. Read once on
  // mount; the only thing that can change it afterward is this same click,
  // which updates the state directly rather than re-reading.
  const [remindersPermission, setRemindersPermission] = useState<ReminderPermission>(() =>
    reminderPermission(),
  );
  // `?` and `g` then a letter (agora#221): wired once here, not per section,
  // since both belong to the shell rather than to whatever place is open.
  // Called unconditionally -- ahead of the early return below -- the same
  // as the state hooks above it.
  const { dialogOpen, openDialog, closeDialog } = useGlobalShortcuts({
    go,
    agoraId: current?.id ?? '',
    chatAvailable: chatAvailable === true,
  });
  // A phone has no keyboard to list shortcuts for.
  const wide = useMediaQuery(MEDIA_QUERIES.md);
  // Before a company is open there is nothing to be in: the paper and a board.
  if (!current || !section) {
    return <>{children}</>;
  }

  // `null` or `0` draws no badge at all, not a zero -- `PlaceRail.Place`'s
  // own `!!badge` check (agora#219).
  const unreadBySection: Partial<Record<Section, number | null | undefined>> = {
    mail: mailUnread,
    chat: chatUnread,
    calendar: calendarUnread,
  };

  return (
    <AppFrame
      rail={
        <PlaceRail label={`${current.name} places`}>
          {PLACES.filter(
            (p) =>
              (p.section !== 'chat' || chatEnabled(current)) &&
              (p.section !== 'tasks' || tasksEnabled(current)),
          ).map((p) => {
            // A place can stay in the rail while whatever backs it decides
            // whether this account may open it -- Chat's own reasoning
            // (agora#280), generalised in `offReason` above.
            const off = offReason(p.section, chatAvailable, filesAvailable);
            if (off !== null) {
              // The reason is the design `Tooltip`, with the tile as its
              // trigger: it opens on hover and on keyboard focus, which a
              // `title` on a wrapper never did for a tile that cannot take
              // either. `place-tip` (`app.css`) gives the trigger the tile's
              // own rail slot and keeps the dimmed button from eating the
              // pointer. Files gets its `href` so it is the same `<a>` the
              // enabled tile is.
              return (
                <Tooltip
                  key={p.section}
                  className="place-tip"
                  label={off}
                  mark={
                    <PlaceRail.Place
                      icon={p.icon}
                      label={p.label}
                      href={
                        p.section === 'files' ? pathForSection(current.id, p.section) : undefined
                      }
                      disabled
                    />
                  }
                >
                  {off}
                </Tooltip>
              );
            }
            const href = pathForSection(current.id, p.section);
            const here = section === p.section;
            return (
              // `PlaceRail.Place` renders either a real `<a href>` or a
              // `<button onClick>`, never both (its own `href`/`onClick`
              // split) -- so `href` alone would lose in-app routing to a
              // full reload, and `onClick` alone (the first adoption pass)
              // lost the real anchor a middle-click, Cmd/Ctrl-click,
              // Shift-click and Alt-click all need, the `follow` behaviour
              // the pre-adoption rail had. `display: contents` keeps this
              // span out of the rail's flex layout, so `PlaceRail.Place`'s
              // own anchor is still the flex item `placerail.css` sizes;
              // the span exists only to intercept a plain click before the
              // anchor's default navigation runs.
              // biome-ignore lint/a11y/noStaticElementInteractions: not a control of its own -- the real interactive element is PlaceRail.Place's own anchor, nested inside; see the comment above.
              // biome-ignore lint/a11y/useKeyWithClickEvents: keyboard activation of the nested anchor already fires a click event, which this handler also catches -- see isPlainClick's own doc for why `button === 0` holds for that case too.
              <span
                key={p.section}
                style={{ display: 'contents' }}
                onClick={(event) => {
                  if (!isPlainClick(event)) return;
                  event.preventDefault();
                  go(href);
                }}
              >
                <PlaceRail.Place
                  icon={p.icon}
                  label={p.label}
                  href={href}
                  current={here}
                  badge={unreadBySection[p.section] ?? undefined}
                />
              </span>
            );
          })}
        </PlaceRail>
      }
      header={
        // A pure CSS scope, not a layout box of its own -- `AppFrame`'s own
        // `.app-frame-header` already lays this out as a flex row, so
        // `display: contents` lets its children take part in that row
        // directly. It exists so the phone breakpoint above can reach the
        // account menu's and the actions' own buttons without reaching every
        // `.btn`/`.ident` in the app.
        <div className="shell-header contents">
          <Company agoras={agoras} current={current} section={section} go={go} />
          <span className="shell-slash flex-none text-muted" aria-hidden="true">
            /
          </span>
          <h2 className="shell-title">{title ?? TITLES[section]}</h2>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: not a control -- the blur only closes a phone's open field when focus leaves it for somewhere else. */}
          <div
            ref={searchRef}
            className="shell-search"
            data-open={searchOpen || undefined}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setSearchOpen(false);
            }}
          >
            <SearchField
              agoraId={current.id}
              section={section}
              companyName={current.name}
              clients={searchClients ?? { mail: null, calendar: null, chat: null, files: null }}
              go={go}
            />
          </div>
          <div className="shell-grow flex-1" />
          <Button
            kind="ghost"
            iconOnly
            className="shell-search-toggle"
            aria-label="Search"
            onPress={() => setSearchOpen(true)}
          >
            <Icon name="search" size={18} />
          </Button>
          {actions}
          {account && (
            <Menu
              trigger={
                // The disc and a chevron, the Archon console's account
                // button -- no name in the header. `account-trigger` keeps
                // the class the focus handling and the menu's opener look up.
                <Button
                  kind="ghost"
                  className="account-trigger shell-account"
                  aria-label={`Your account, ${account.address}`}
                >
                  <span
                    className="shell-account-disc"
                    style={stickerOf(hueOf(account.address || '?'))}
                    aria-hidden="true"
                  >
                    {initialsOf(account.name ?? '', account.address)}
                  </span>
                  <Icon name="chev-down" size={14} />
                </Button>
              }
              label="Your account"
              placement="bottom end"
              info={
                <span className="flex flex-col">
                  {account.name && <span className="text-sm font-strong">{account.name}</span>}
                  <span className="text-xs text-muted">{account.address}</span>
                </span>
              }
              items={[
                // Holding more than one company is the desktop app's own
                // trick: it is org-independent, where a browser tab is already
                // on the company that served it. So the web build never offers
                // to add a second. The `/add` route itself stays reachable by
                // its URL, for the case of a link naming a company this
                // browser has not added.
                ...(isDesktop()
                  ? [
                      {
                        id: 'add',
                        label: 'Add a company',
                        icon: 'plus' as const,
                        onAction: () => go(pathForAdd()),
                      },
                    ]
                  : []),
                // The desktop app picks its dock icon in its own app menu.
                ...(window.wtfalchDesktop
                  ? []
                  : [
                      {
                        id: 'app-icon',
                        label: 'App icon',
                        icon: 'palette' as const,
                        items: APP_ICONS.map((choice) => ({
                          id: `app-icon-${choice.id}`,
                          label: (
                            <span className="agora-icon-choice inline-flex items-center gap-2">
                              <img src={`/icons/${choice.id}.svg`} alt="" />
                              {choice.label}
                              {choice.id === appIcon && <Icon name="check" size="sm" />}
                            </span>
                          ),
                          onAction: () => {
                            applyAppIcon(choice.id);
                            setAppIcon(choice.id);
                          },
                        })),
                      },
                    ]),
                {
                  id: 'settings',
                  label: 'Settings',
                  icon: 'settings' as const,
                  onAction: () => go(pathForSettings(current.id)),
                },
                ...(wide
                  ? [
                      {
                        id: 'shortcuts',
                        label: 'Keyboard shortcuts',
                        icon: 'question' as const,
                        onAction: () =>
                          openDialog(
                            document.querySelector<HTMLElement>('.shell-header .account-trigger'),
                          ),
                      },
                    ]
                  : []),
                // D7: offered only while permission is still undecided --
                // granted or denied, this account already answered and
                // nothing here asks again. Covers meeting reminders and chat
                // notifications alike (see `remindersPermission`'s own
                // comment above): one browser permission, one ask.
                ...(remindersPermission === 'default'
                  ? [
                      {
                        id: 'reminders',
                        label: 'Turn on notifications',
                        icon: 'bell' as const,
                        onAction: () => {
                          void requestReminderPermission().then(setRemindersPermission);
                        },
                      },
                    ]
                  : []),
                ...(onSignOut
                  ? [
                      {
                        id: 'sign-out',
                        label: 'Sign out',
                        icon: 'logout' as const,
                        separated: true,
                        onAction: onSignOut,
                      },
                    ]
                  : []),
              ]}
            />
          )}
        </div>
      }
    >
      <CallDock current={window.location.pathname} go={go} />
      <div className="flex flex-1 min-h-0">{children}</div>
      {dialogOpen && <ShortcutsDialog onClose={closeDialog} phone={!wide} />}
    </AppFrame>
  );
}

/** The company's mark -- its initial on its own colour -- and its name. With
 *  more than one company in this browser, the name is the switch, and it keeps
 *  the section: somebody in one company's mail who switches wants the other
 *  company's mail, not its front page. */
function Company({
  agoras,
  current,
  section,
  go,
}: {
  agoras: Agora[];
  current: Agora;
  section: Section;
  go: (path: string) => void;
}) {
  const mark = (
    <OrgMark
      initial={current.name.slice(0, 1).toUpperCase()}
      color={current.theme.accent}
      size="sm"
    />
  );

  if (agoras.length < 2) {
    return (
      <span className="shell-company relative inline-flex flex-none items-center gap-2 text-sm font-strong">
        {mark}
        <span className="shell-company-name">{current.name}</span>
      </span>
    );
  }

  return (
    <Menu
      label="Switch company"
      placement="bottom start"
      trigger={
        <Button
          kind="ghost"
          className="shell-company shell-company-trigger"
          aria-label={`Company, ${current.name}`}
        >
          <OrgMark
            initial={current.name.slice(0, 1).toUpperCase()}
            color={current.theme.accent}
            size="sm"
            className="shell-company-mark"
          />
          <span className="shell-company-name">{current.name}</span>
          <Icon name="chev-down" size={14} />
        </Button>
      }
      items={agoras.map((a) => ({
        id: a.id,
        label: (
          <span className="shell-company-choice inline-flex items-center gap-2">
            <OrgMark initial={a.name.slice(0, 1).toUpperCase()} color={a.theme.accent} size="sm" />
            {a.name}
            {a.id === current.id && <Icon name="check" size="sm" />}
          </span>
        ),
        onAction: () => go(pathForSection(a.id, section)),
      }))}
    />
  );
}
